import { useQuery } from '@tanstack/react-query';
import { portalAPI } from '@/lib/api/portal';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn, formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Check, Calendar, MapPin, Clock, Users, Sparkles } from 'lucide-react';
import type { SpecialEvent } from '@/types';

interface SpecialEventsStepProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onComplete: () => void;
  onBack: () => void;
  isEventRegistrant?: boolean;
}

export function SpecialEventsStep({
  selectedIds,
  onChange,
  onComplete,
  onBack,
  isEventRegistrant = false,
}: SpecialEventsStepProps) {
  const { data: specialEventsData, isLoading } = useQuery({
    queryKey: ['special-events', { status: 'published', visible: true }],
    queryFn: () => portalAPI.getSpecialEvents({ status: 'published', visible: true }),
  });

  const specialEvents: SpecialEvent[] = specialEventsData?.docs ?? [];

  const toggleSpecialEvent = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((eid) => eid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const getPrice = (event: SpecialEvent) => {
    const now = new Date();
    const isEarlyBird =
      event.earlyBirdTicketPrice &&
      event.earlyBirdStartDate &&
      event.earlyBirdEndDate &&
      now >= new Date(event.earlyBirdStartDate) &&
      now <= new Date(event.earlyBirdEndDate);

    if (isEarlyBird) return event.earlyBirdTicketPrice!;
    if (isEventRegistrant && event.eventRegistrantPrice) return event.eventRegistrantPrice;
    return event.ticketPrice || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading special events...</div>
      </div>
    );
  }

  if (specialEvents.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-muted-foreground">No special events available at this time.</p>
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button onClick={onComplete}>Skip & Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold">Special Events</h2>
        <p className="text-muted-foreground mt-2">
          Join us for exclusive evening events and social gatherings
        </p>
      </div>

      <div className="grid gap-6">
        {specialEvents.map((event) => {
          const isSelected = selectedIds.includes(event.id);
          const available =
            event.maxCapacity !== undefined && event.maxCapacity !== null
              ? event.maxCapacity - event.currentRegistrations - event.reservedRegistrations
              : Infinity;
          const isFull = available <= 0;
          const price = getPrice(event);

          const now = new Date();
          const isEarlyBird =
            event.earlyBirdTicketPrice &&
            event.earlyBirdStartDate &&
            event.earlyBirdEndDate &&
            now >= new Date(event.earlyBirdStartDate) &&
            now <= new Date(event.earlyBirdEndDate);

          return (
            <Card
              key={event.id}
              className={cn(
                'cursor-pointer transition-all overflow-hidden',
                isSelected ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50',
                isFull && 'opacity-60 cursor-not-allowed'
              )}
              onClick={() => !isFull && toggleSpecialEvent(event.id)}
            >
              {event.heroImage?.url && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={event.heroImage.url}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-heading font-bold text-white">{event.title}</h3>
                    {event.subtitle && (
                      <p className="text-white/80 text-sm">{event.subtitle}</p>
                    )}
                  </div>
                </div>
              )}

              <CardHeader className={event.heroImage?.url ? 'pt-4' : ''}>
                {!event.heroImage?.url && (
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        {event.title}
                      </CardTitle>
                      {event.subtitle && <CardDescription>{event.subtitle}</CardDescription>}
                    </div>
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                    </div>
                  </div>
                )}

                {event.heroImage?.url && (
                  <div className="flex items-start justify-between">
                    <div className="flex flex-wrap gap-2">
                      {isEarlyBird && <Badge variant="success">Early Bird</Badge>}
                      {isEventRegistrant && event.eventRegistrantPrice && (
                        <Badge variant="secondary">Registrant Price</Badge>
                      )}
                      {event.dressCode && <Badge variant="outline">{event.dressCode}</Badge>}
                    </div>
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                    </div>
                  </div>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {event.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {formatDate(event.eventDate)}
                  </div>
                  {event.startTime && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {formatTime(event.startTime)}
                      {event.endTime && ` - ${formatTime(event.endTime)}`}
                    </div>
                  )}
                  {(event.location || event.venue) && (
                    <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                      <MapPin className="w-4 h-4" />
                      {event.venue || event.location}
                    </div>
                  )}
                  {event.maxCapacity && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span className={cn(isFull ? 'text-destructive' : '')}>
                        {isFull ? 'Sold Out' : `${available} spots left`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Itinerary preview */}
                {event.itinerary && event.itinerary.length > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                      Event Schedule
                    </p>
                    <div className="space-y-1">
                      {event.itinerary.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground w-16">{item.time}</span>
                          <span>{item.title}</span>
                        </div>
                      ))}
                      {event.itinerary.length > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{event.itinerary.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{formatCurrency(price)}</span>
                    {isEarlyBird && event.ticketPrice && (
                      <span className="text-sm text-muted-foreground line-through">
                        {formatCurrency(event.ticketPrice)}
                      </span>
                    )}
                  </div>
                  {isFull ? (
                    <Badge variant="destructive">Sold Out</Badge>
                  ) : isSelected ? (
                    <Badge variant="success">Selected</Badge>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onComplete}>
          {selectedIds.length > 0
            ? `Continue with ${selectedIds.length} event${selectedIds.length > 1 ? 's' : ''}`
            : 'Skip & Continue'}
        </Button>
      </div>
    </div>
  );
}
